import { RHF } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";


import { PlusIcon, Trash2Icon } from "lucide-react";
import type { Control } from "react-hook-form";
import { useFieldArray, useWatch } from "react-hook-form";
import type { BranchScheduleFormValues } from "./branch-schedule-types";

function newBlackoutEntry(): BranchScheduleFormValues["blackouts"][number] {
  return {
    id: crypto.randomUUID(),
    date: new Date().toISOString().slice(0, 10),
    allDay: true,
    startTime: "",
    endTime: "",
    reason: "",
  };
}

function BlackoutRow({
  control,
  index,
  fieldId,
  onRemove,
}: {
  control: Control<BranchScheduleFormValues>;
  index: number;
  fieldId: string;
  onRemove: () => void;
}) {
  const allDay = useWatch({
    control,
    name: `blackouts.${index}.allDay`,
  });

  return (
    <div className="flex flex-col gap-3 rounded-[12px] border bg-muted/20 p-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="min-w-[140px] flex-1">
        <label
          htmlFor={`blackout-date-${fieldId}`}
          className="mb-1 block font-medium text-muted-foreground text-xs"
        >
          ວັນທີ
        </label>
        <RHF.Controller
          name={`blackouts.${index}.date`}
          control={control}
          render={({ field }) => (
            <input
              id={`blackout-date-${fieldId}`}
              type="date"
              className="h-9 w-full rounded-[12px] border border-input bg-background px-2 text-sm"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>

      <RHF.Controller
        name={`blackouts.${index}.allDay`}
        control={control}
        render={({ field }) => (
          <label
            htmlFor={`blackout-allday-${fieldId}`}
            className="flex h-9 cursor-pointer items-center gap-2"
          >
            <Checkbox
              id={`blackout-allday-${fieldId}`}
              checked={field.value ?? true}
              onCheckedChange={(v) => field.onChange(v === true)}
            />
            <span className="text-sm">ທັງວັນ</span>
          </label>
        )}
      />

      {!allDay ? (
        <>
          <div>
            <label
              htmlFor={`blackout-start-${fieldId}`}
              className="mb-1 block font-medium text-muted-foreground text-xs"
            >
              ເລີ່ມ
            </label>
            <RHF.Controller
              name={`blackouts.${index}.startTime`}
              control={control}
              render={({ field }) => (
                <input
                  id={`blackout-start-${fieldId}`}
                  type="time"
                  className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
          <div>
            <label
              htmlFor={`blackout-end-${fieldId}`}
              className="mb-1 block font-medium text-muted-foreground text-xs"
            >
              ສິ້ນສຸດ
            </label>
            <RHF.Controller
              name={`blackouts.${index}.endTime`}
              control={control}
              render={({ field }) => (
                <input
                  id={`blackout-end-${fieldId}`}
                  type="time"
                  className="h-9 w-28 rounded-[12px] border border-input bg-background px-2 text-sm"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
        </>
      ) : null}

      <div className="min-w-[160px] flex-1">
        <label
          htmlFor={`blackout-reason-${fieldId}`}
          className="mb-1 block font-medium text-muted-foreground text-xs"
        >
          ເຫດຜົນ (ທາງເລືອກ)
        </label>
        <RHF.Controller
          name={`blackouts.${index}.reason`}
          control={control}
          render={({ field }) => (
            <input
              id={`blackout-reason-${fieldId}`}
              type="text"
              placeholder="ຕົວຢ່າງ: ບຳລຸງຮັກສາ"
              className="h-9 w-full rounded-[12px] border border-input bg-background px-2 text-sm"
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0 text-destructive hover:text-destructive"
        onClick={onRemove}
        aria-label="ລຶບ blackout"
      >
        <Trash2Icon className="h-4 w-4" />
      </Button>
    </div>
  );
}

type BranchBlackoutEditorProps = {
  control: Control<BranchScheduleFormValues>;
};

export function BranchBlackoutEditor({ control }: BranchBlackoutEditorProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "blackouts",
  });

  return (
    <div className="space-y-3">
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          ຍັງບໍ່ມີວັນຫຼືຊ່ວງເວລາທີ່ປິດຮັບອໍເດີ
        </p>
      ) : (
        fields.map((field, index) => (
          <BlackoutRow
            key={field.id}
            control={control}
            index={index}
            fieldId={field.id}
            onRemove={() => remove(index)}
          />
        ))
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => append(newBlackoutEntry())}
      >
        <PlusIcon className="h-4 w-4" />
        ເພີ່ມວັນປິດຮັບອໍເດີ
      </Button>
    </div>
  );
}

export function BranchBlackoutEditorCard({
  control,
}: BranchBlackoutEditorProps) {
  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-6 py-4">
        <h3 className="font-heading font-semibold text-lg">Blackout</h3>
        <p className="text-muted-foreground text-sm">
          ວັນຫຼືຊ່ວງເວລາທີ່ບໍ່ຮັບອໍເດີ pickup
        </p>
      </div>
      <div className="p-6">
        <BranchBlackoutEditor control={control} />
      </div>
    </div>
  );
}
